/**
 * perf — misura baseline web-vitals IN LOCALE (build plan §3, M0: "web-vitals in locale + primo
 * numero registrato"). D44 = budget: LCP ≤ 2s · INP ≤ 100ms · CLS ≤ 0.05 (shell a parte).
 *
 * Regole del progetto: ZERO telemetria (build plan §2 principio 3) — i numeri vivono nella status
 * line e in `localStorage` (`hypr.perf.v1`), mai su un analytics server. Il record conserva
 * `baseline` (il PRIMO run mai visto: il numero da battere) e `latest` (ultimo run): niente magia,
 * tutto ispezionabile da chi usa l'app.
 */
import { createSignal } from "solid-js";
import { onCLS, onINP, onLCP, type Metric } from "web-vitals";

export const PERF_KEY = "hypr.perf.v1";

export interface PerfNumbers {
  lcp_ms: number | null;
  inp_ms: number | null;
  cls: number | null;
  at: string; // ISO 8601 (D12)
}

export interface PerfRecord {
  baseline: PerfNumbers; // primo run: intoccabile una volta scritto
  latest: PerfNumbers;
}

function empty(): PerfNumbers {
  return { lcp_ms: null, inp_ms: null, cls: null, at: new Date().toISOString() };
}

const [numbers, setNumbers] = createSignal<PerfNumbers>(empty());
const [rtMs, setRtMs] = createSignal<number | null>(null);

/**
 * Autodiagnostica della status line (bug device 2026-09-25): il `?` perenne nascondeva QUALE
 * gate cadeva. Qui esponiamo ciò che il motore SA fare, con la STESSA grammatica di web-vitals:
 * - `support.inp` = gate onINP.js (PerformanceEventTiming + interactionId sul prototipo);
 * - `et` = contatore NOSTRO di entry `event` + quante hanno interactionId != 0 (il prototipo può
 *   esistere mentre le entry arrivano tutte iid=0: WebKit bug 297361 / PR #49304);
 * - `tapPainted` = un tap ha completato il suo paint (double-rAF di measureReaction).
 */
export interface EtCounts {
  entries: number;
  attributed: number;
}

export interface PerfDiag {
  et: EtCounts | null; // null = il motore non produce/accetta entry `event`
  tapPainted: boolean;
}

export const PERF_SUPPORT = (() => {
  const types: readonly string[] =
    typeof PerformanceObserver !== "undefined" ? (PerformanceObserver.supportedEntryTypes ?? []) : [];
  return {
    lcp: types.includes("largest-contentful-paint"),
    cls: types.includes("layout-shift"),
    // gate IDENTICO a web-vitals onINP.js: senza interactionId l'INP non è attribuibile
    inp: !!(globalThis.PerformanceEventTiming && "interactionId" in PerformanceEventTiming.prototype),
  };
})();

const [diag, setDiag] = createSignal<PerfDiag>({ et: null, tapPainted: false });

/** Stato diagnostico corrente (signal Solid). */
export function perfDiag(): PerfDiag {
  return diag();
}

/** Tempo di reazione locale (tap → paint): vedi measureReaction. */
export function perfRt(): number | null {
  return rtMs();
}

/**
 * Tempo di reazione deterministico tap → paint: misurato NOI con performance.now + due rAF,
 * nessuna dipendenza da Event Timing (il 2026-09-24 INP è risultato muto su iOS 27 reale pur
 * funzionando sull'harness WebKit 26.6 con lo stesso build). Serve come numero locale per il
 * budget D44 (INP ≤ 100ms) su QUALUNQUE motore — niente magia, niente "?" per sempre.
 */
export function measureReaction(): void {
  const t0 = performance.now();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setRtMs(Math.round(performance.now() - t0));
      // il paint del tap è avvenuto: se a questo punto il motore non ha prodotto ALCUNA entry
      // `event`, lo diciamo ("n/a (nessuna entry)") invece di restare `?` per sempre
      setDiag((d) => ({ ...d, tapPainted: true }));
    });
  });
}

/** Numeri correnti (signal Solid: la status line si aggiorna da sola). */
export function perfNumbers(): PerfNumbers {
  return numbers();
}

/** Ultimo record salvato in localStorage (baseline + latest), o null se mai misurato. */
export function readRecord(): PerfRecord | null {
  try {
    const raw = localStorage.getItem(PERF_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw) as Partial<PerfRecord>;
    if (!obj.baseline || !obj.latest) return null;
    return { baseline: obj.baseline, latest: obj.latest };
  } catch {
    // record corrotto/JSON sporco = nessuna misura: mai eccezione nuda, mai magia
    return null;
  }
}

function save(latest: PerfNumbers): void {
  try {
    const prev = readRecord();
    const rec: PerfRecord = { baseline: prev?.baseline ?? latest, latest };
    localStorage.setItem(PERF_KEY, JSON.stringify(rec));
  } catch {
    /* localStorage pieno/off: la misura resta visibile in status line */
  }
}

function report(metric: Metric): void {
  const next: PerfNumbers = { ...numbers(), at: new Date().toISOString() };
  // ms interi per LCP/INP (il budget D44 è in secondi/ms), 3 decimali per CLS (D44 ≤ 0.05)
  const value = metric.name === "CLS" ? Math.round(metric.value * 1000) / 1000 : Math.round(metric.value);
  if (metric.name === "LCP") next.lcp_ms = value;
  else if (metric.name === "INP") next.inp_ms = value;
  else if (metric.name === "CLS") next.cls = value;
  setNumbers(next);
  save(next);
}

/**
 * Soglia per le entry `event`: `0` è FUORI SPEC (MDN: "Lowest possible threshold is 16ms",
 * arrotondata a multipli di 8). Un motore può rifiutarlo con TypeError e web-vitals lo
 * ingoierebbe nel try/catch di observe() → metrica morta in silenzio (bug device 2026-09-25).
 * Si prova 0 (massima permissività dove è accettato), si cade su 16 (minimo di spec).
 * Verificato sul probe: con soglia 16 arrivano comunque entry da 16ms (confronto >=).
 */
function eventThreshold(): 0 | 16 {
  try {
    new PerformanceObserver(() => {}).observe({ type: "event", buffered: true, durationThreshold: 0 });
    return 0;
  } catch {
    return 16;
  }
}

/** Contatore NOSTRO di entry `event` (indipendente da web-vitals): la parte "si ispeziona". */
function startEventCounter(threshold: 0 | 16): void {
  const types: readonly string[] = PerformanceObserver.supportedEntryTypes ?? [];
  if (!types.includes("event")) return; // et resta null = "no", deterministico
  const po = new PerformanceObserver((list) => {
    const counts: EtCounts = { entries: 0, attributed: 0 };
    for (const e of list.getEntries()) {
      counts.entries += 1;
      const iid = (e as PerformanceEventTiming).interactionId ?? 0;
      if (iid !== 0) counts.attributed += 1;
    }
    setDiag((d) => ({ ...d, et: { entries: (d.et?.entries ?? 0) + counts.entries, attributed: (d.et?.attributed ?? 0) + counts.attributed } }));
  });
  try {
    po.observe({ type: "event", buffered: true, durationThreshold: threshold });
    setDiag((d) => ({ ...d, et: { entries: 0, attributed: 0 } }));
  } catch {
    /* osservazione rifiutata: et resta null = "no" — mai eccezione nuda */
  }
}

/** Avvia la raccolta. Chiamare PRIMA del render: LCP/CLS si osservano dall'inizio del load. */
export function startPerf(): void {
  // reportAllChanges: con il default i valori "finali" di INP/CLS arrivano solo a page-hide —
  // che su un web clip iOS ucciso dal task switcher NON parte mai (regressione 2026-09-24:
  // INP restava "?" anche premendo i pulsanti). Qui servono numeri VIVI in status line:
  // non-finali finché la pagina non si chiude, ma veri e aggiornati a ogni interazione/shift.
  const opts = { reportAllChanges: true } as const;
  const threshold = eventThreshold();
  startEventCounter(threshold);
  onLCP(report, opts);
  // DEFAULT 40 = le interazioni più rapide di 40ms NON producono proprio l'entry (sorgente
  // web-vitals onINP.js). Su questo prodotto i tap durano ~16ms: serve la soglia minima.
  // Il valore esatto è eventThreshold() (0 dove accettato, 16 = minimo di spec altrove).
  onINP(report, { ...opts, durationThreshold: threshold });
  onCLS(report, opts);
}
